import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { s } from '../components/PeriodStatsPage.styles';
import { downloadAsExcel } from '../../../utils/downloadAsExcel';
import { useDragScroll } from '../../../utils/useDragScroll';

type UsageAuctionOverviewProps = {
  periodSummary: string;
  region: string;
  isDetail: boolean;
  onToggleDetail: () => void;
  usage: string;
  usageDetail?: string;
  court?: string;
  usageData?: UsageRow[];
  loading?: boolean;
  error?: string | null;
};

export type UsageRow = {
  usage: string;
  progressed_items: string | number;
  successful_items: string | number;
  success_rate: string | number;
  price_success_rate: string | number;
  avg_bidders: string | number;
};

// 화면용 정규화 타입은 그대로 사용 가능 (원하면 이름 UsageMetricsItem 유지)
type UsageMetricsItem = {
  id: string;
  usage: string;
  progress: number;
  wonCount: number;
  winRate: number; // %
  winPriceRate: number; // %
  avgBidders: number;
};

export default function UsageAuctionOverview({
  periodSummary,
  region,
  isDetail,
  onToggleDetail,
  usage,
  usageDetail,
  court,
  usageData,
  loading,
  error,
}: UsageAuctionOverviewProps) {
  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  type SortKey =
    | 'progress'
    | 'wonCount'
    | 'winRate'
    | 'winPriceRate'
    | 'avgBidders';
  type SortDir = 'asc' | 'desc';

  const [sortKey, setSortKey] = useState<SortKey>('progress');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const toNum = (v: unknown) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return v;
    const n = Number(String(v).replace(/,/g, ''));
    return isNaN(n) ? 0 : n;
  };

  const source: UsageMetricsItem[] = useMemo(() => {
    const rows = Array.isArray(usageData) ? usageData : [];
    return rows.map((r, idx) => ({
      id: `${idx}-${r.usage}`,
      usage: r.usage ?? '',
      progress: toNum(r.progressed_items),
      wonCount: toNum(r.successful_items),
      winRate: toNum(r.success_rate),
      winPriceRate: toNum(r.price_success_rate),
      avgBidders: toNum(r.avg_bidders),
    }));
  }, [usageData]);

  const rows = useMemo(() => {
    const base = [...source];
    base.sort((a, b) => {
      const va = a[sortKey] as number;
      const vb = b[sortKey] as number;
      const diff = (va ?? 0) - (vb ?? 0);
      return sortDir === 'asc' ? diff : -diff;
    });
    return base;
  }, [source, sortKey, sortDir]);

  const avg = useMemo(() => {
    const n = rows.length || 1;
    const sum = rows.reduce(
      (a, c) => ({
        progress: a.progress + c.progress,
        wonCount: a.wonCount + c.wonCount,
        winRate: a.winRate + c.winRate,
        winPriceRate: a.winPriceRate + c.winPriceRate,
        avgBidders: a.avgBidders + c.avgBidders,
      }),
      { progress: 0, wonCount: 0, winRate: 0, winPriceRate: 0, avgBidders: 0 },
    );
    return {
      progress: Math.round(sum.progress / n),
      wonCount: Math.round(sum.wonCount / n),
      winRate: +(sum.winRate / n).toFixed(1),
      winPriceRate: +(sum.winPriceRate / n).toFixed(1),
      avgBidders: +(sum.avgBidders / n).toFixed(1),
    };
  }, [rows]);

  const handleDownloadExcel = () => {
    const xlsxRows = rows.map((r) => ({
      용도: r.usage,
      진행건수: r.progress,
      낙찰건수: r.wonCount,
      '낙찰률(%)': r.winRate,
      '낙찰가율(%)': r.winPriceRate,
      '평균 응찰자 수': r.avgBidders,
    }));
    downloadAsExcel(xlsxRows as any[], '용도별_경매_지표');
  };

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#f0e8ff">
            <img src="/assets/images/icon/purple_apt.svg" alt="" />
          </s.Img>
          <s.ButtonDiv>
            <s.ExcelButton onClick={onToggleDetail} aria-label="상세 용도 전환">
              <img src="/assets/images/icon/rotation.svg" alt="" />
            </s.ExcelButton>
            <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
              <img src="/assets/images/icon/download-black.svg" alt="" />
            </s.ExcelButton>
          </s.ButtonDiv>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>용도별 경매 지표 현황</h3>
          <p>용도별 경매 시장 현황 비교</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>{periodSummary}</s.SelectedOption>
            <s.SelectedOption>
              {court && court !== 'all' ? court : `${region} 지역`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>
      {loading ? (
        <LoaderWrap>
          <LoaderImg src="/assets/images/icon/loader.gif" alt="용도별" />
        </LoaderWrap>
      ) : (
        <s.TableWrap>
          <TableContainer
            ref={ref}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
          >
            <Table>
              <thead>
                <tr>
                  <s.Th align="left" width="160px">
                    용도
                  </s.Th>
                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('progress')}>
                      <Arrow $active={sortKey === 'progress'} $dir={sortDir} />
                      진행건수
                    </SortHeader>
                  </Th>
                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('wonCount')}>
                      <Arrow $active={sortKey === 'wonCount'} $dir={sortDir} />
                      낙찰건수
                    </SortHeader>
                  </Th>
                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('winRate')}>
                      <Arrow $active={sortKey === 'winRate'} $dir={sortDir} />
                      낙찰률
                    </SortHeader>
                  </Th>
                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('winPriceRate')}>
                      <Arrow
                        $active={sortKey === 'winPriceRate'}
                        $dir={sortDir}
                      />
                      낙찰가율
                    </SortHeader>
                  </Th>
                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('avgBidders')}>
                      <Arrow
                        $active={sortKey === 'avgBidders'}
                        $dir={sortDir}
                      />
                      평균 응찰자 수
                    </SortHeader>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <s.Td align="left">{row.usage}</s.Td>
                    <s.Td align="right">
                      {row.progress.toLocaleString('ko-KR')}
                    </s.Td>
                    <s.Td align="right">
                      {row.wonCount.toLocaleString('ko-KR')}
                    </s.Td>
                    <s.Td align="right">{row.winRate.toFixed(1)}%</s.Td>
                    <s.Td align="right">{row.winPriceRate.toFixed(1)}%</s.Td>
                    <s.Td align="right">{row.avgBidders.toFixed(1)}</s.Td>
                  </tr>
                ))}

                <s.AvgTr>
                  <s.TdAvg align="left">평균</s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.progress.toLocaleString('ko-KR')}
                  </s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.wonCount.toLocaleString('ko-KR')}
                  </s.TdAvg>
                  <s.TdAvg align="right">{avg.winRate.toFixed(1)}%</s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.winPriceRate.toFixed(1)}%
                  </s.TdAvg>
                  <s.TdAvg align="right">{avg.avgBidders.toFixed(1)}</s.TdAvg>
                </s.AvgTr>
              </tbody>
            </Table>
          </TableContainer>
        </s.TableWrap>
      )}
    </s.Wrap>
  );
}

const Table = styled.table`
  min-width: max(760px, 100%);
  table-layout: auto;
  border-collapse: separate;
  border-spacing: 0;
  white-space: nowrap;

  thead th {
    background: #f4f5f7;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  thead th:first-child {
    border-top-left-radius: 8px;
  }

  thead th:last-child {
    border-top-right-radius: 8px;
  }

  tbody tr:not(:last-child) td {
    border-bottom: 1px solid #e8e9ed;
  }
`;

const Th = styled.th<{ align?: 'left' | 'center' | 'right' }>`
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  angle: 0 deg;
  opacity: 1;
  gap: 4px;
  padding: 12px 14px 12px 12px;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
`;

const SortHeader = styled.button`
  font-weight: 500;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
`;

const Arrow = styled.img.attrs<{ $active?: boolean; $dir?: 'asc' | 'desc' }>(
  ({ $active, $dir }) => {
    if ($active) {
      return {
        src:
          $dir === 'desc'
            ? '/assets/images/arrow/blue_arrow_down.svg' //  활성 + 내림
            : '/assets/images/arrow/red_arrow_up.svg', //  활성 + 오름
        alt: '',
      };
    }
    return { src: '/assets/images/arrow/gray_arrow_down.svg', alt: '' }; // 비활성
  },
)<{ $active?: boolean; $dir?: 'asc' | 'desc' }>`
  width: 16px;
  height: 16px;
  transition:
    transform 120ms ease,
    opacity 120ms ease;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;
